import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { environment } from 'src/environments/environment';

interface FavouriteReportItem {
  listingId: number;
  name: string;
  favouriteCount: number;
}

interface FavouriteReportPage {
  data: FavouriteReportItem[];
  pageEvent: { pageIndex: number; pageSize: number; length: number };
}

interface FavouriteExportPage {
  data: FavouriteReportItem[];
  nextCursor: number | null;
}

@Component({
  selector: 'reporting-dashboard',
  templateUrl: 'component.html',
  styleUrls: ['component.scss'],
  standalone: false,
})
export class ReportingDashboard implements OnInit, OnDestroy {
  public items: FavouriteReportItem[] = [];
  public loading = false;
  public loadError = false;
  public pageIndex = 0;
  public pageSize = 25;
  public totalItems = 0;
  public downloading = false;
  public downloadedItems = 0;
  private latestPageRequest = 0;
  private exportRun = 0;

  constructor(private Api: ApiService) {}

  ngOnInit(): void {
    void this.loadPage();
  }

  ngOnDestroy(): void {
    this.latestPageRequest++;
    this.cancelDownload();
  }

  public async loadPage(
    pageIndex = 0,
    pageSize = this.pageSize,
  ): Promise<void> {
    const request = ++this.latestPageRequest;
    this.loading = true;
    this.loadError = false;
    this.items = [];
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    try {
      const page: FavouriteReportPage =
        await this.Api.getWithoutPageEventFilter(
          'admin/reports/favourites',
          { pageIndex, pageSize },
          environment.adminUrl,
        );
      if (request !== this.latestPageRequest) return;
      this.items = page.data;
      this.totalItems = page.pageEvent.length;
    } catch (error) {
      if (request !== this.latestPageRequest) return;
      this.loadError = true;
      this.totalItems = 0;
      void this.Api.handleError(error);
    } finally {
      if (request === this.latestPageRequest) this.loading = false;
    }
  }

  public cancelDownload(): void {
    this.exportRun++;
    this.downloading = false;
  }

  public async downloadCSV(): Promise<void> {
    if (this.downloading) {
      return;
    }

    const run = ++this.exportRun;
    this.downloading = true;
    this.downloadedItems = 0;
    try {
      const items: FavouriteReportItem[] = [];
      let afterId = 0;
      // Keep requests sequential: avoid repeatedly ranking the whole table or
      // flooding the DB with parallel export queries. Download only on success.
      while (true) {
        const page: FavouriteExportPage =
          await this.Api.getWithoutPageEventFilter(
            'admin/reports/favourites',
            { mode: 'export', afterId },
            environment.adminUrl,
          );
        if (run !== this.exportRun) return;
        items.push(...page.data);
        this.downloadedItems = items.length;
        if (page.nextCursor === null) break;
        if (
          !Number.isSafeInteger(page.nextCursor) ||
          page.nextCursor <= afterId
        ) {
          throw new Error('The report could not be completed. Please retry.');
        }
        afterId = page.nextCursor;
      }
      items.sort(
        (a, b) =>
          b.favouriteCount - a.favouriteCount || a.listingId - b.listingId,
      );
      // Include the header as a row so an empty report still downloads a CSV.
      const rows = [
        ['Name', 'Favourite count'],
        ...items.map(({ name, favouriteCount }) => [
          // Keep content titles from being interpreted as spreadsheet formulas.
          /^\s*[=+\-@]|^[\t\r\n]/.test(name) ? `'${name}` : name,
          favouriteCount,
        ]),
      ];
      CsvExportService.export(rows, 'favorites-report', { showLabels: false });
    } catch (error) {
      if (run === this.exportRun) void this.Api.handleError(error);
    } finally {
      if (run === this.exportRun) this.downloading = false;
    }
  }
}
